import videojs from 'video.js';

import { v4 as uuid } from 'uuid';

import TimelineMarker from './timeline-markers/timeline-marker.js';

const _timelineMarkers = new WeakMap();

/**
 * @typedef {Object} TimelineMarkerConfig
 * @alias TimelineMarkerConfig
 * @memberof TimelineMarkersPlugin
 * @property {string} [id] An ID by which to identify the marker.
 * @property {number} time The time in seconds.
 * @property {string} label A label that will be visible in the UI.
 * @property {string} [color] A color with which to display the marker.
 * @property {string} [shape] The inline html svg text for the marker.
 */

/**
 * The TimelineMarkers module.
 * @member {TimelineMarkersPlugin} timelineMarkers
 * @memberof MediasitePlayer#
 */

/**
 * Configures clickable markers that are displayed on the seekbar.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class TimelineMarkersPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);
        _timelineMarkers.set(this, {});
    }
    /**
     * 
     * @param {TimelineMarkerConfig} markerConfig A marker to add to the timeline.
     * @returns {string} The marker's ID.
     * @example
     * const markerId = player.timelineMarkers.add({
     *     time: 10,
     *     label: 'Marker at 10 seconds'
     * });
     */
    add(markerConfig) {
        const { time, label, color, shape } = markerConfig;
        const id = markerConfig.id ?? uuid();

        const timelineMarker = new TimelineMarker(this.player, { time, label, color, shape });
        const controlBar = this.player.getChild('ControlBar');
        controlBar.addChild(timelineMarker);
        _timelineMarkers.get(this)[id] = timelineMarker;

        this.#sortAll();

        return id;
    }

    /**
     * Removes a marker from the seekbar.
     * @param {string} id The marker's unique ID.
     * @example
     * player.timelineMarkers.remove('markerId');
     */
    remove(id) {
        const marker = _timelineMarkers.get(this)[id];
        if (marker) {
            const controlBar = this.player.getChild('ControlBar');
            controlBar.removeChild(marker);
            delete _timelineMarkers.get(this)[id];
            this.#sortAll();
        }
    }

    /**
     * Updates the timeline marker with the given ID.
     * @param {string} id
     * @param {Object} data
     * @param {number} data.time The time in seconds.
     * @param {string} data.label A label for the marker.
     * @param {string} data.color A color for the marker.
     * @param {string} data.shape The inline html svg for the marker icon.
     * @example
     * player.timelineMarkers.update('markerId', { });
     */
    update(id, data) {
        const { time, label, color, shape } = (data ?? {});

        const marker = _timelineMarkers.get(this)[id];
        if (marker) {
            // Update timeline marker
            if (time) {
                marker.time = time;
            }
            if (label) {
                marker.label = label;
            }
            if (color) {
                marker.color = color;
            }
            if (shape) {
                marker.shape = shape;
            }
            marker.hidden = !data;
        }

        this.#sortAll();
    }

    /**
     * Gets the data for the timeline marker with the given ID.
     * @param {string} id
     * @returns {{text:string,time:number,color:string,path:string,viewBox:string}}
     * @example
     * const marker = player.timelineMarkers.get('markerId');
     */
    get(id) {
        const marker = _timelineMarkers.get(this)[id];
        if (marker) {
            return {
                text: marker.label,
                time: marker.time,
                color: marker.color,
                shape: marker.shape
            }
        }
    }

    #sortAll() {
        const timelineMarkers = Object.values(_timelineMarkers.get(this)).sort((a, b) => {
            if (a.time > b.time) {
                return 1;
            }
            else if (a.time < b.time) {
                return 0;
            }
            return 0;
        });

        const controlBar = this.player.getChild('ControlBar');
        for (const timelineMarker of timelineMarkers) {
            controlBar.addChild(timelineMarker);
        }
    }

    dispose() {
        const timelineMarkers = _timelineMarkers.get(this);
        for (const markerId of Object.keys(timelineMarkers)) {
            this.remove(markerId);
        }

        super.dispose();
    }
}

export default TimelineMarkersPlugin;
